import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { callIfNeeded, needsData } from 'common/modules/async-actions/thunk';
import {
    requestWalletAds,
    selectWalletAdsAsyncState,
} from 'common/features/store/duck/wallet-ads/duck';

/**
 * Hook that returns a function to request wallet ads if needed.
 * Uses callIfNeeded to prevent redundant API calls within a 10-minute window.
 *
 * @returns A function that can be dispatched to request wallet ads if needed
 */
export const useRequestWalletAdsIfNeeded = () => {
    const dispatch = useDispatch<any>();
    const asyncState = useSelector(selectWalletAdsAsyncState);

    const requestWalletAdsIfNeeded = useMemo(
        () =>
            callIfNeeded(
                requestWalletAds,
                needsData({
                    state: asyncState,
                    maxAge: 1000 * 60 * 10,
                })
            ),
        [asyncState]
    );

    return useMemo(
        () => () => {
            try {
                dispatch(requestWalletAdsIfNeeded());
            } catch (error) {
                // Wallet ads are non-critical - log error but don't throw
                // This ensures wallet functionality continues even if ads fail
                console.error('Failed to request wallet ads:', error);
            }
        },
        [dispatch, requestWalletAdsIfNeeded]
    );
};
